import axios from 'axios';
import { API_BASE_URL } from '@/lib/constants';
import { useAuthStore } from '@/store/auth.store';
import { usePortalAuthStore } from '@/store/portal-auth.store';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // sends httpOnly refreshToken cookie automatically
});

// Attach access token to every request; inject X-Vendor-Tenant-Id for portal routes
apiClient.interceptors.request.use((config) => {
  const url = config.url ?? '';
  if (url.includes('/portal/')) {
    const { sessionToken, selectedTenantId } = usePortalAuthStore.getState();
    if (sessionToken) config.headers.Authorization = `Bearer ${sessionToken}`;
    if (selectedTenantId) config.headers['X-Vendor-Tenant-Id'] = selectedTenantId;
  } else {
    const token = useAuthStore.getState().accessToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 by attempting token refresh
let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

apiClient.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config as typeof err.config & { _retry?: boolean };

    if (err.response?.status === 401 && !originalRequest._retry) {
      // Some 401s are credential/token-gate outcomes on PUBLIC flows, NOT session expiry:
      //   - reverification_required : login gate (surface the "check your email" panel in-place)
      //   - setup_token_invalid     : MFA-enrollment start/confirm with a bad/expired setup token
      //                               (public endpoints; MfaEnrollmentPage must show its toast)
      // For these there is no session to refresh; let the 401 propagate to the component's
      // onError so it renders the right message in-place — a refresh/hard-redirect would wipe
      // React state / eat the toast. Keyed on the server's error CODE (not a URL) so it cannot
      // over-match an AUTHENTICATED endpoint: a real session-expiry 401 never carries these
      // codes, so those still fall through to the refresh logic below, byte-identical.
      const exemptCode = err.response?.data?.errors?.[0]?.code;
      if (exemptCode === 'reverification_required' || exemptCode === 'setup_token_invalid') {
        return Promise.reject(err);
      }

      // Public (unauthenticated) auth endpoints: a 401 here is a credential/gate outcome
      // (bad password, invalid MFA code, reverification), NOT a session expiry — there is no
      // session to refresh. Never refresh/hard-redirect these; let the 401 propagate to the
      // mutation's onError so the login component surfaces the right message in-place (the
      // hard-redirect otherwise reloads the page and eats the error toast). Keyed on the
      // request URL so genuine expired-session 401s on AUTHENTICATED API calls still refresh
      // as today.
      const reqUrl = originalRequest?.url ?? '';
      if (reqUrl.includes('/auth/login') || reqUrl.includes('/auth/mfa/challenge')) {
        return Promise.reject(err);
      }

      // Portal session expiry: only logout + redirect when the failing request itself
      // was a portal resource (/portal/ in URL). Peripheral 401s (e.g. /views without
      // portal JWT) must not trigger logout — let the component handle gracefully.
      // Symmetric with request interceptor which uses url.includes('/portal/') to pick
      // the auth token (D-SU.4.0.4-3).
      if (
        window.location.pathname.startsWith('/portal') &&
        err.config.url?.includes('/portal/')
      ) {
        usePortalAuthStore.getState().logout();
        window.location.href = '/portal/login';
        return Promise.reject(err);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshQueue.push((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Cookie is sent automatically — no body needed
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
        const newAccess = data.data.accessToken;

        useAuthStore.getState().setAccessToken(newAccess);

        refreshQueue.forEach((cb) => cb(newAccess));
        refreshQueue = [];

        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return apiClient(originalRequest);
      } catch {
        refreshQueue = [];
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  },
);

export async function refreshTokens(): Promise<boolean> {
  try {
    // Cookie is sent automatically — no body needed
    const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
    const { accessToken } = data.data;

    // Fetch full user profile after refresh
    const meRes = await axios.get(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      withCredentials: true,
    });
    const user = meRes.data.data;

    useAuthStore.getState().login(accessToken, user);
    return true;
  } catch {
    return false;
  }
}
